import { createFileRoute, Link } from "@tanstack/react-router";
import { useInfiniteQuery } from "@tanstack/react-query";
import { runsApi } from "@/api/runs";
import { StatusBadge } from "@/components/status-badge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EmptyState } from "@/components/empty-state";
import { formatRelative, formatRunDuration } from "@/lib/format";
import { POLL, isRunActive } from "@/lib/poll";
import { CalendarClock, FileText } from "lucide-react";
import { useState } from "react";
import type { RunStatus } from "@/api/types";

export const Route = createFileRoute("/_authed/runs/")({
  component: RunsPage,
});

const STATUSES: RunStatus[] = ["pending", "running", "waiting", "completed", "failed", "cancelled"];

type Trigger = "event" | "cron" | "";
const TRIGGERS: { value: Trigger; label: string }[] = [
  { value: "event", label: "Events" },
  { value: "cron", label: "Scheduled" },
  { value: "", label: "All triggers" },
];

function RunsPage() {
  const [status, setStatus] = useState<RunStatus | "">("");
  // Default to event-driven runs so the constant cron flood doesn't bury them;
  // scheduled runs live on the Schedules page (or switch this filter).
  const [trigger, setTrigger] = useState<Trigger>("event");

  const q = useInfiniteQuery({
    queryKey: ["runs", status, trigger],
    queryFn: ({ pageParam }) =>
      runsApi.list({ status: status || undefined, trigger: trigger || undefined, cursor: pageParam, limit: 50 }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor || undefined,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return POLL.active;
      const hasActive = data.pages[0]?.data.some((r) => isRunActive(r.status));
      return hasActive ? POLL.active : POLL.idle;
    },
  });

  const runs = q.data?.pages.flatMap((p) => p.data) ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="mb-1 font-mono text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
            Monitor
          </div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">Runs</h1>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Trigger</span>
            <Select value={trigger || "all"} onValueChange={(v) => setTrigger(v === "all" ? "" : (v as Trigger))}>
              <SelectTrigger className="h-8 w-36" aria-label="Filter by trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TRIGGERS.map((t) => (
                  <SelectItem key={t.value || "all"} value={t.value || "all"}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Status</span>
            <Select
              value={status || "all"}
              onValueChange={(v) => setStatus(v === "all" ? "" : (v as RunStatus))}
            >
              <SelectTrigger className="h-8 w-40 capitalize" aria-label="Filter by status">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <Card className="overflow-hidden">
        {runs.length === 0 && !q.isLoading ? (
          <EmptyState
            icon={<FileText size={32} />}
            title="No runs match"
            description="Send an event or wait for a cron trigger."
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-5 font-mono text-[11px] uppercase tracking-wider">Status</TableHead>
                <TableHead className="font-mono text-[11px] uppercase tracking-wider">Function</TableHead>
                <TableHead className="font-mono text-[11px] uppercase tracking-wider">Event</TableHead>
                <TableHead className="text-right font-mono text-[11px] uppercase tracking-wider">Attempt</TableHead>
                <TableHead className="text-right font-mono text-[11px] uppercase tracking-wider">Duration</TableHead>
                <TableHead className="pr-5 text-right font-mono text-[11px] uppercase tracking-wider">Started</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {runs.map((r) => (
                <TableRow key={r.id} className="group cursor-pointer">
                  <TableCell className="pl-5"><StatusBadge status={r.status} /></TableCell>
                  <TableCell>
                    <Link to="/runs/$id" params={{ id: r.id }}
                      className="font-mono text-[13px] font-medium text-foreground group-hover:text-brand">
                      {r.function_slug}
                    </Link>
                  </TableCell>
                  <TableCell className="font-mono text-[13px] text-muted-foreground">
                    {r.event_id ? (
                      r.event_name ?? "—"
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full border border-brand/30 bg-brand/10 px-2 py-0.5 text-[11px] font-medium text-brand">
                        <CalendarClock size={11} /> Scheduled
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="tabular text-right font-mono text-[13px] text-muted-foreground">{r.attempt}</TableCell>
                  <TableCell className="tabular text-right font-mono text-[13px] text-muted-foreground">
                    {formatRunDuration(r.started_at, r.completed_at)}
                  </TableCell>
                  <TableCell className="tabular pr-5 text-right font-mono text-[13px] text-muted-foreground">
                    {formatRelative(r.started_at)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {q.hasNextPage && (
          <div className="border-t p-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => void q.fetchNextPage()}
              disabled={q.isFetchingNextPage}
            >
              {q.isFetchingNextPage ? "Loading…" : "Load more"}
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
